import { ForwardFocusIcon, PlayFocusIcon } from '@/components/card/focus-icon';
import { CardVariant } from '@/components/card/types';
import { ChipLabel } from '@/components/chip-label';
import { ContributorAvatar } from '@/components/contributor/avatar';
import { ImageDecorationProps } from '@/components/focusable-overlaid-image/image-decoration';
import { ImageDecorationPosition } from '@/components/focusable-overlaid-image/types';
import { DOT_STRING_SPACER } from '@/utils/string';
import { isBanner, isCarousel } from '@rhg/components/card';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { useProgramItemCountText } from '@rhg/hooks/use-program-item-count-text';
import commonMessages from '@rhg/lang/messages/common';
import { sxAppend } from '@rhg/libs/material/sx';
import { ProgramState } from '@rhg/utils/programs';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ProgramContextAwareProgramCardProps } from '..';
import { styleCarouselContent } from '../..';
import { CompletedStatus } from '../../completed-status';
import { TopLeftDecoration } from '../top-left-decoration';
import { useVariantAgnosticProgramCardProps } from '../use-variant-agnostic-program-card-props';

const styleImageIcon = (spacing: number) => ({ m: spacing });

const styleImageCompletedIcon = { width: 24, height: 24 };

type VariantAwareProgramCardProps = Pick<
    ProgramContextAwareProgramCardProps,
    | 'variant'
    | 'isTopRecommendation'
    | 'isCarouselCategoryDescriptionDisplayed'
    | 'carouselInProgressTitleMaxLines'
    | 'isContributorAvatarDisplayedOnCarousel'
    | 'contentType'
    | 'additionalAction'
    | 'hideImageOverlays'
    | 'isNotClickable'
    | 'displayGuideProgress'
    | 'imageIconTop'
    | 'imageSideRadiusEnabled'
    | 'trackProps'
    | 'shouldShowSessionLength'
    | 'contentTagOverride'
    | 'showNarratorAvatar'
>;

/**
 * Do not use this hook outside of ProgramCard and its dependencies.
 */
export function useVariantAwareProgramCardProps({
    variant,
    isTopRecommendation = false,
    isCarouselCategoryDescriptionDisplayed,
    carouselInProgressTitleMaxLines = 1,
    isContributorAvatarDisplayedOnCarousel,
    contentType,
    additionalAction,
    hideImageOverlays,
    isNotClickable,
    displayGuideProgress,
    imageIconTop,
    imageSideRadiusEnabled,
    trackProps,
    shouldShowSessionLength = false,
    contentTagOverride,
    showNarratorAvatar,
}: VariantAwareProgramCardProps) {
    const {
        programState,
        isClinicalProgram,
        numSessions,
        progressText,
        inProgressDTC,
        sessionAndGuideLengthText,
        shouldShowAvgSessionLength,
        inProgressClinical,
        durationText,
        program,
    } = useVariantAgnosticProgramCardProps({
        isTopRecommendation,
        contentType,
        isNotClickable,
        displayGuideProgress,
        trackProps,
        shouldShowSessionLength,
    });
    const { formatMessage } = useIntl();
    const isMobile = useIsMobile();
    const itemCountText = useProgramItemCountText(program?.meditationType);

    const sxImageIcon = useMemo(
        () => styleImageIcon(variant === 'horizontal' ? 2 : 4),
        [variant]
    );

    const sessionsText = useMemo(() => {
        if (
            variant !== CardVariant.BannerV3 &&
            (isTopRecommendation || shouldShowSessionLength)
        ) {
            if (isMobile) return '';
            return sessionAndGuideLengthText;
        }
        return itemCountText(numSessions);
    }, [
        variant,
        isTopRecommendation,
        itemCountText,
        numSessions,
        isMobile,
        sessionAndGuideLengthText,
        shouldShowSessionLength,
    ]);

    const imageDecorations: ImageDecorationProps[] = useMemo(() => {
        if (hideImageOverlays) {
            return [];
        }

        const decorations: ImageDecorationProps[] = [];

        // Top left decoration
        decorations.push({
            position: ImageDecorationPosition.TopLeft,
            children: (
                <TopLeftDecoration
                    programState={programState}
                    isTopRecommendation={isTopRecommendation}
                    variant={variant}
                    isClinicalProgram={isClinicalProgram}
                    displayCategoryDescriptionOnCarousel={
                        isCarouselCategoryDescriptionDisplayed
                    }
                    numSessions={numSessions}
                    meditationType={program?.meditationType}
                    sessionsText={sessionsText}
                    progressText={progressText}
                    sxImageIcon={sxImageIcon}
                    inProgressDTC={inProgressDTC}
                    imageIconTop={imageIconTop}
                />
            ),
        });

        if (variant === CardVariant.BannerV3 && additionalAction) {
            decorations.push({
                position: ImageDecorationPosition.TopRight,
                isInteractive: true,
                children: additionalAction,
            });
        }

        if (programState === ProgramState.Completed && !isBanner(variant)) {
            decorations.push({
                position: ImageDecorationPosition.BottomLeft,
                children: (
                    <CompletedStatus
                        sx={sxAppend(sxImageIcon, styleImageCompletedIcon)}
                        size="small"
                    />
                ),
            });
        } else if (
            shouldShowAvgSessionLength &&
            isMobile &&
            variant !== CardVariant.BannerV3
        ) {
            decorations.push({
                position: ImageDecorationPosition.BottomLeft,
                children: (
                    <ChipLabel
                        label={sessionAndGuideLengthText}
                        variant="darkTransparent"
                        sx={sxImageIcon}
                    />
                ),
            });
        }

        return decorations;
    }, [
        hideImageOverlays,
        programState,
        isTopRecommendation,
        variant,
        isClinicalProgram,
        isCarouselCategoryDescriptionDisplayed,
        numSessions,
        program?.meditationType,
        sessionsText,
        progressText,
        sxImageIcon,
        inProgressDTC,
        imageIconTop,
        additionalAction,
        shouldShowAvgSessionLength,
        isMobile,
        sessionAndGuideLengthText,
    ]);

    const contentTag = useMemo(() => {
        if (contentTagOverride) {
            return contentTagOverride;
        }
        const alwaysTopVariants: CardVariant[] = [
            CardVariant.Banner,
            CardVariant.BannerV2,
        ];
        return isTopRecommendation &&
            (alwaysTopVariants.includes(variant) ||
                (!isMobile && variant === CardVariant.BannerV3))
            ? formatMessage(commonMessages['common.top_recommendation'])
            : undefined;
    }, [
        contentTagOverride,
        isTopRecommendation,
        variant,
        isMobile,
        formatMessage,
    ]);

    const categoryDescription = useMemo(() => {
        switch (programState) {
            case ProgramState.InProgress:
                // DTC progress is tracked but never displayed (EVE-13082) —
                // an in-progress DTC pack (programState can be InProgress
                // for DTC too when it has a completed guide) shows session
                // count instead.
                if (!isClinicalProgram) {
                    return !isCarousel(variant) ||
                        isCarouselCategoryDescriptionDisplayed
                        ? sessionsText
                        : undefined;
                }
                if (isCarousel(variant)) {
                    return isCarouselCategoryDescriptionDisplayed
                        ? progressText(variant === 'carouselSquare')
                        : undefined;
                }
                if (variant === CardVariant.BannerV3) {
                    return `${progressText(
                        false
                    )}${DOT_STRING_SPACER}${durationText}`;
                }
                return progressText(false);

            case ProgramState.Abandoned:
            case ProgramState.Completed:
            case ProgramState.NotJoined:
                return !isCarousel(variant) ||
                    isCarouselCategoryDescriptionDisplayed
                    ? sessionsText
                    : undefined;

            // DTC programs, which have no program state
            default:
                if (
                    variant === 'carousel' ||
                    (variant === 'carouselSquare' &&
                        typeof isCarouselCategoryDescriptionDisplayed ===
                            'boolean')
                ) {
                    return isCarouselCategoryDescriptionDisplayed
                        ? sessionsText
                        : undefined;
                }

                if (
                    isBanner(variant) ||
                    (variant === 'horizontal' && !isMobile)
                ) {
                    return sessionsText;
                }

                return undefined;
        }
    }, [
        durationText,
        isCarouselCategoryDescriptionDisplayed,
        isClinicalProgram,
        isMobile,
        programState,
        progressText,
        sessionsText,
        variant,
    ]);

    const titleIcon = useMemo(() => {
        if (
            showNarratorAvatar ||
            (programState !== ProgramState.InProgress &&
                (isBanner(variant) ||
                    (isCarousel(variant) &&
                        isContributorAvatarDisplayedOnCarousel)))
        ) {
            return <ContributorAvatar contributor={program?.narrator} />;
        }
        return undefined;
    }, [
        showNarratorAvatar,
        isContributorAvatarDisplayedOnCarousel,
        program?.narrator,
        programState,
        variant,
    ]);

    const focusIcon = useMemo(() => {
        if (isNotClickable) {
            return undefined;
        }
        if (
            variant !== CardVariant.BannerV2 &&
            variant !== CardVariant.Banner
        ) {
            if (inProgressClinical || inProgressDTC) {
                return <PlayFocusIcon variant={variant} />;
            } else {
                return <ForwardFocusIcon variant={variant} />;
            }
        }
        return undefined;
    }, [inProgressClinical, inProgressDTC, isNotClickable, variant]);

    const titleMaxLines = useMemo(() => {
        if (isCarousel(variant)) {
            return inProgressClinical || inProgressDTC
                ? carouselInProgressTitleMaxLines
                : 2;
        }

        if (variant === 'horizontal') {
            return inProgressClinical || inProgressDTC ? 1 : 2;
        }

        return undefined;
    }, [
        carouselInProgressTitleMaxLines,
        inProgressClinical,
        inProgressDTC,
        variant,
    ]);

    const defaultContentSx = useMemo(
        () => (isCarousel(variant) ? styleCarouselContent : undefined),
        [variant]
    );

    const isImageSideRadiusEnabled = useMemo(
        () => imageSideRadiusEnabled ?? variant !== 'horizontal',
        [imageSideRadiusEnabled, variant]
    );

    const isEntireCardClickable =
        !isNotClickable && variant !== CardVariant.Banner;

    return {
        isEntireCardClickable,
        isImageSideRadiusEnabled,
        defaultContentSx,
        titleMaxLines,
        focusIcon,
        titleIcon,
        categoryDescription,
        contentTag,
        imageDecorations,
    };
}
